import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Col from "components/ui-elements/Col";
import PopupDialog from "components/dialog/PopupDialog";
import MyFormEditable from "components/form-elements/MyFormEditable";
import useAxiosFunction from "hooks/useAxiosFunction";

const DropdownDinamisCol = (props) => {
  const {
    colsize,
    model,
    margin,

    name,
    label,
    placeholder,
    options,
    optionsUrl = "",
    optionsAuth = true,
    valueId,
    labelId,
    register,
    validation,
    error,
    className,
    setValue,
    value,
    defaultValue,

    searchable = true,
    otherEnabled = true,
    otherLabel = "Lainnya",
    add_config = null,

    ...rest
  } = props;

  const addConfig = add_config || null;
  const disabled = rest?.disabled === true;
  const resolvedOtherLabel = addConfig?.otherLabel ?? otherLabel;
  const resolvedOptionsUrl = addConfig?.optionsUrl ?? optionsUrl;
  const resolvedOptionsAuth = addConfig?.optionsAuth ?? optionsAuth;
  const resolvedOtherFormUrl = addConfig?.url ?? "";
  const resolvedOtherFormCellId = addConfig?.cellid ?? "id";
  const resolvedOtherFormColumns = addConfig?.columns ?? null;
  const resolvedOtherFormColtype = addConfig?.coltype ?? null;
  const resolvedOtherFormTitle = addConfig?.title ?? "";
  const otherEnabledFinal =
    otherEnabled &&
    Boolean(resolvedOtherFormUrl) &&
    Boolean(resolvedOtherFormColtype) &&
    Array.isArray(resolvedOtherFormColumns) &&
    resolvedOtherFormColumns.length > 0;

  const [optionsState, setOptionsState] = useState(
    Array.isArray(options) ? options : [],
  );
  const [, , , axiosFetch] = useAxiosFunction();
  const [query, setQuery] = useState("");
  const [isOther, setIsOther] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [otherDialogOpen, setOtherDialogOpen] = useState(false);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const appliedDefaultRef = useRef(null);

  useEffect(() => {
    setOptionsState(Array.isArray(options) ? options : []);
  }, [options]);

  const refetchOptions = useCallback(async () => {
    if (!resolvedOptionsUrl) return null;
    const resp = await axiosFetch({
      method: "GET",
      url: resolvedOptionsUrl,
      auth: resolvedOptionsAuth,
    });
    const next = resp?.data;
    if (Array.isArray(next)) {
      setOptionsState(next);
      return next;
    }
    return null;
  }, [axiosFetch, resolvedOptionsAuth, resolvedOptionsUrl]);

  useEffect(() => {
    refetchOptions();
  }, [refetchOptions]);

  const filteredOptions = useMemo(() => {
    const list = Array.isArray(optionsState) ? optionsState : [];
    const selectableList = otherEnabledFinal
      ? list.filter(
          (opt) =>
            String(opt?.[labelId] || "").trim().toLowerCase() !==
            String(resolvedOtherLabel || "").trim().toLowerCase(),
        )
      : list;
    if (!searchable) return selectableList;
    const q = (query || "").trim().toLowerCase();
    if (!q) return selectableList;
    return selectableList.filter((opt) => {
      const lbl = opt?.[labelId];
      return (lbl ?? "").toString().toLowerCase().includes(q);
    });
  }, [labelId, optionsState, otherEnabledFinal, query, resolvedOtherLabel, searchable]);

  useEffect(() => {
    if (!name) return;

    let shouldApplyDefault = defaultValue !== undefined;
    let selected = defaultValue;
    if (defaultValue && typeof defaultValue === "object" && !Array.isArray(defaultValue)) {
      shouldApplyDefault = Object.prototype.hasOwnProperty.call(defaultValue, name);
      selected = shouldApplyDefault ? (defaultValue?.[name] ?? "") : "";
    }
    if (!shouldApplyDefault) return;

    const selectedKey = JSON.stringify(selected ?? "");
    if (appliedDefaultRef.current === selectedKey) return;

    appliedDefaultRef.current = selectedKey;
    setSelectedValue(selected ?? "");
    setValue?.(name, selected ?? "");
  }, [defaultValue, name, setValue]);

  useEffect(() => {
    if (!name || value === undefined) return;
    const selected = value ?? "";
    const selectedKey = JSON.stringify(selected);
    if (appliedDefaultRef.current === selectedKey) return;

    appliedDefaultRef.current = selectedKey;
    setSelectedValue(selected);
  }, [name, value]);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => searchInputRef.current?.focus?.(), 0);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const el = wrapperRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectOption = (value) => {
    setSelectedValue(value);
    setIsOpen(false);
    setIsOther(false);
    setValue?.(name, value);
  };

  const selectOther = () => {
    setSelectedValue("");
    setIsOpen(false);
    setIsOther(true);
    setValue?.(name, "");
    setOtherDialogOpen(true);
  };

  const showOtherField = otherEnabledFinal && isOther;

  const selectedLabel = useMemo(() => {
    if (!selectedValue) return "";
    const list = Array.isArray(optionsState) ? optionsState : [];
    const found = list.find(
      (opt) => String(opt?.[valueId]) === String(selectedValue),
    );
    return found?.[labelId] ?? "";
  }, [labelId, optionsState, selectedValue, valueId]);

  const closeOtherDialog = () => {
    setOtherDialogOpen(false);
  };

  const cancelOther = () => {
    setOtherDialogOpen(false);
    setIsOther(false);
  };

  const onOtherSuccess = async (hasil, reqdata) => {
    closeOtherDialog();
    const nextOptions = (await refetchOptions()) ?? optionsState;
    const createdLabel = reqdata?.[labelId];
    if (createdLabel) {
      const found = nextOptions?.find?.(
        (opt) => String(opt?.[labelId]) === String(createdLabel),
      );
      if (found?.[valueId]) {
        selectOption(found[valueId]);
      }
    }
  };

  return (
    <Col size={colsize} model={model} margin={margin}>
      {label && <label>{label}</label>}

      <input type="hidden" {...register?.(name, validation)} />

      <div ref={wrapperRef} className="dropdown">
        <button
          type="button"
          className={`form-control dropdown-toggle text-left ${error ? "is-invalid" : ""} ${className || ""}`}
          disabled={disabled}
          onClick={() => setIsOpen((v) => !v)}
        >
          {selectedLabel || placeholder || ""}
        </button>
        {isOpen && (
          <div
            className="dropdown-menu show w-100"
            style={{ maxHeight: 320, overflowY: "auto" }}
          >
            {searchable && (
              <div className="px-3 py-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cari..."
                />
              </div>
            )}
            {filteredOptions.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                className="dropdown-item"
                onClick={() => selectOption(opt?.[valueId])}
              >
                {opt?.[labelId]}
              </button>
            ))}
            {otherEnabledFinal && (
              <>
                <div className="dropdown-divider" />
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={selectOther}
                >
                  {resolvedOtherLabel}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {error && <span className="error invalid-feedback">{error}</span>}

      {showOtherField && (
        <PopupDialog
          title={resolvedOtherFormTitle}
          size="lg"
          show={otherDialogOpen}
          allowClose={false}
        >
          <MyFormEditable
            title={""}
            url={resolvedOtherFormUrl}
            cellid={resolvedOtherFormCellId}
            coltype={resolvedOtherFormColtype}
            columns={resolvedOtherFormColumns}
            lblsave="Simpan"
            lblcancel="Batal"
            onCancel={cancelOther}
            onSuccess={onOtherSuccess}
          />
        </PopupDialog>
      )}
    </Col>
  );
};

DropdownDinamisCol.defaultProps = {
  colsize: 12,
  model: "md",
  margin: "mb-1",
  name: "",
  label: "",
  placeholder: "",
  options: [],
  valueId: "",
  labelId: "",
  validation: {},
  error: "",
  className: "",
  setValue: () => {},
  register: () => ({}),
};

export default DropdownDinamisCol;
